import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Menu, X, Users, Database, Phone, UserCog, ChevronRight, 
  UserCheck, Mail, Save, XCircle, Upload, Paperclip, Trash2, 
  AlertCircle, Copy, FileText, ChevronDown, UserCircle, 
  MessageCircle, Settings, LogOut, Calculator, BarChart } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';
import ContactRequests from '../components/ContactRequests';
import UserManagement from '../components/UserManagement';
import DatabaseSettings from '../components/DatabaseSettings';
import EstimationCoefficients from '../components/EstimationCoefficients';
import ClientList from '../components/admin/ClientList';
import ClientDetailsPanel from '../components/admin/ClientDetailsPanel';
import Statistics from '../components/admin/Statistics';
import type { FormSubmission, ClientDetails, Comment } from '../types';

type AdminSection = 'clients' | 'contact-requests' | 'statistics' | 'settings';
type SettingsSection = 'users' | 'database' | 'coefficients';

export default function AdminPage() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [activeSection, setActiveSection] = useState<AdminSection>('clients');
  const [activeSettingsSection, setActiveSettingsSection] = useState<SettingsSection>('users');
  const [showSettingsSubmenu, setShowSettingsSubmenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Client management state
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [selectedClient, setSelectedClient] = useState<FormSubmission | null>(null);
  const [clientDetails, setClientDetails] = useState<ClientDetails>({ comments: [], files: [] });
  const [comments, setComments] = useState<Comment | null>(null);
  
  // Get current user from session storage
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';

  useEffect(() => {
    if (activeSection === 'clients') {
      fetchSubmissions();
    }
  }, [activeSection]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientDetails(selectedClient.id);
    }
  }, [selectedClient]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setSubmissions(data || []);
      setConnectionError(false);
    } catch (error) {
      console.error('Erreur:', error);
      setConnectionError(true);
      toast.error('Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetails = async (submissionId: string) => {
    try {
      const response = await fetch(`/api/get-client-details?submissionId=${submissionId}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Accept': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const html = await response.text();
        console.error('❌ Réponse non JSON :', html);
        throw new Error(`Réponse inattendue: ${contentType}`);
      }

      const data = await response.json();

      setComments(data.comments?.[0] || null);
      setClientDetails({
        ...data.formSubmission,
        comments: data.comments || [],
        files: data.files || [],
      });
    } catch (error) {
      console.error('❌ Error fetching client details:', error);
      toast.error('Erreur lors du chargement des détails client');
    }
  };

  const handleDeleteFile = async (fileId: string, filePath: string) => {
    try {
      const response = await fetch('/api/delete-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileId, filePath })
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      await fetchClientDetails(selectedClient!.id);
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error deleting file');
    }
  };

  const handleDeleteEstimation = (id: string) => {
    // Remove from local state
    setSubmissions(prev => prev.filter(sub => sub.id !== id));
    // If the deleted estimation was selected, clear the selection
    if (selectedClient?.id === id) {
      setSelectedClient(null);
    }
  };

  const handleSignOut = () => {
    setShowLogoutConfirm(true);
  };

  const confirmSignOut = () => {
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('user');
    navigate('/');
  };

  if (!isAuthenticated) {
    return <Navigate to="/login\" replace />;
  }

  const menuItems = [
    { icon: Users, label: 'Gestion Clients', section: 'clients' as const },
    { icon: MessageCircle, label: 'Demandes de Contact', section: 'contact-requests' as const },
    { icon: BarChart, label: 'Statistiques', section: 'statistics' as const },
    { 
      icon: Settings, 
      label: 'Paramètres', 
      section: 'settings' as const,
      submenu: [
        { icon: UserCog, label: 'Gestion Utilisateurs', section: 'users' as const },
        { icon: Database, label: 'Base de données', section: 'database' as const },
        { icon: Calculator, label: 'Coef. Estimations', section: 'coefficients' as const }
      ]
    }
  ];

  const handleSectionClick = (section: AdminSection) => {
    if (section === 'settings') {
      setShowSettingsSubmenu(!showSettingsSubmenu);
    } else {
      setActiveSection(section);
      setShowSettingsSubmenu(false);
    }
  };

  const handleSettingsSectionClick = (section: SettingsSection) => {
    setActiveSettingsSection(section);
    setActiveSection('settings');
    if (window.innerWidth < 1024) {
      setIsMenuOpen(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'settings':
        return (
          <div className="space-y-6">
            {activeSettingsSection === 'users' && <UserManagement />}
            {activeSettingsSection === 'database' && <DatabaseSettings />}
            {activeSettingsSection === 'coefficients' && <EstimationCoefficients />}
          </div>
        );

      case 'clients':
        return (
          <ClientList
            submissions={submissions}
            onSelect={setSelectedClient}
            loading={loading}
            connectionError={connectionError}
            onRetry={fetchSubmissions}
            onDelete={handleDeleteEstimation}
          />
        );

      case 'contact-requests':
        return <ContactRequests />;
        
      case 'statistics':
        return <Statistics />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Toaster />
      
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la déconnexion</h3>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir vous déconnecter ?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmSignOut}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {!isMenuOpen && (
        <button
          onClick={() => setIsMenuOpen(true)}
          className="fixed top-4 left-4 z-30 p-2 rounded-md bg-white shadow-lg hover:bg-gray-100"
        >
          <Menu className="h-6 w-6 text-gray-600" />
        </button>
      )}
      
      <div 
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out z-30`}
      >
        <div className="p-4 flex justify-between items-center border-b">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        
        <nav className="p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.section}>
                <button
                  onClick={() => handleSectionClick(item.section)}
                  className={`flex items-center w-full p-3 rounded-lg hover:bg-indigo-50 group ${
                    (activeSection === item.section || (item.section === 'settings' && showSettingsSubmenu))
                      ? 'bg-indigo-50 text-indigo-600' 
                      : 'text-gray-700 hover:text-indigo-600'
                  }`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  <span className="flex-1">{item.label}</span>
                  {item.submenu && (
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${
                      showSettingsSubmenu ? 'rotate-90' : ''
                    }`} />
                  )}
                </button>
                {item.submenu && showSettingsSubmenu && (
                  <ul className="mt-2 ml-6 space-y-2">
                    {item.submenu.map((subItem) => (
                      <li key={subItem.section}>
                        <button
                          onClick={() => handleSettingsSectionClick(subItem.section)}
                          className={`flex items-center w-full p-2 rounded-lg hover:bg-indigo-50 ${
                            activeSection === 'settings' && activeSettingsSection === subItem.section
                              ? 'bg-indigo-50 text-indigo-600'
                              : 'text-gray-700 hover:text-indigo-600'
                          }`}
                        >
                          <subItem.icon className="h-4 w-4 mr-2" />
                          <span className="text-sm">{subItem.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
      
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      
      <div className={`flex-1 ${isMenuOpen ? 'ml-64' : 'ml-0'} transition-all duration-300`}>
        <div className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center">
                {activeSection === 'clients' ? (
                  <>
                    <UserCheck className="h-8 w-8 text-indigo-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Prospects - Clients</h1>
                  </>
                ) : activeSection === 'contact-requests' ? (
                  <>
                    <MessageCircle className="h-8 w-8 text-indigo-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Demandes de Contact</h1>
                  </>
                ) : activeSection === 'statistics' ? (
                  <>
                    <BarChart className="h-8 w-8 text-indigo-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Statistiques</h1>
                  </>
                ) : activeSection === 'settings' ? (
                  <>
                    <Settings className="h-8 w-8 text-indigo-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
                  </>
                ) : null}
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-gray-700">
                  <UserCircle className="h-6 w-6 mr-2" />
                  <span className="font-medium">{currentUser.username}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </button>
              </div>
            </div>

            {renderContent()}
          </div>
        </div>
      </div>

      {selectedClient && (
        <ClientDetailsPanel
          client={selectedClient}
          clientDetails={clientDetails}
          comments={comments}
          onClose={() => setSelectedClient(null)}
          onRefreshClientDetails={() => fetchClientDetails(selectedClient.id)}
          onDeleteFile={handleDeleteFile}
        />
      )}
    </div>
  );
}